import React from 'react'

import { Switch as SwitchRouterDom, Redirect, Route } from 'react-router-dom'

import { RouteWithLayout } from './../components'
import {
  Auth as AuthLayout,
  Main as MainLayout,
  Subscribe as SubscribeLayout,
  MainContent as MainContentLayout,
} from 'layouts'
import NotFound from 'views/NotFound'
import Subscribe from 'views/Subscribe'
import Contracts from 'views/Contracts'
import Invoices from 'views/Invoices'
import Orders from 'views/Orders'
import Settings from 'views/Settings'
import SettingsAccount from 'views/Settings/components/SettingsAccount'
import SettingsTaxes from 'views/Settings/components/SettingsTaxes'
import SettingsUsers from 'views/Settings/components/SettingsUsers'
import SettingsEmails from 'views/Settings/components/SettingsEmails'
import SettingsProfiles from 'views/Settings/components/SettingsProfiles'
import SettingsPaymentMethods from 'views/Settings/components/SettingsPaymentMethods'
import Plans from 'views/Plans'
import Clients from 'views/Clients'
import Entries from 'views/Entries'
import TaxInvoices from 'views/TaxInvoices'
import Login from 'views/Login'
import LoginToken from 'views/LoginToken'
import PasswordReset from 'views/PasswordReset'
import PasswordChange from 'views/PasswordChange'
import PasswordFirstAccess from 'views/PasswordFirstAccess'
import ContractPlans from 'views/ContractPlans'
import ClientProvider from 'providers/ClientProvider'
import ContractProvider from 'providers/ContractProvider'
import SubscribeProvider from 'providers/SubscribeProvider'
import AuthProvider from 'providers/AuthProvider'
import PaymentMethodsProvider from 'providers/PaymentMethodsProvider'
import ContractPlanMigrateProvider from 'providers/ContractPlanMigrateProvider'
import DetachedInvoiceProvider from 'providers/DetachedInvoiceProvider'
import routes from './routes'

const Switch = () => {
  const subscribeRoutes = React.useMemo(() => [routes.subscribe.toString()], [])

  const loginRoutes = React.useMemo(
    () => [routes.login.toString(), routes.password.toString()],
    [],
  )

  const privateRoutes = React.useMemo(
    () => [
      routes.contracts.toString(),
      routes.contractPlans.toString(),
      routes.plans.toString(),
      routes.invoices.toString(),
      routes.orders.toString(),
      routes.clients.toString(),
      routes.taxInvoices.toString(),
      routes.entries.toString(),
      routes.settings.toString(),
    ],
    [],
  )

  const authRoutes = React.useMemo(() => [...loginRoutes, ...privateRoutes], [
    loginRoutes,
    privateRoutes,
  ])

  return (
    <SwitchRouterDom>
      <RouteWithLayout
        path={subscribeRoutes}
        provider={SubscribeProvider}
        layout={SubscribeLayout}
      >
        <Route path={routes.subscribe.main} component={Subscribe.Main} exact />
        <Route
          path={routes.subscribe.checkout}
          component={Subscribe.Checkout}
          exact
        />
      </RouteWithLayout>

      <Redirect from={routes.root} to={routes.login.main} exact />

      <RouteWithLayout path={authRoutes} provider={AuthProvider}>
        <RouteWithLayout path={loginRoutes} layout={AuthLayout}>
          <RouteWithLayout path={routes.login.main} component={Login} exact />
          <RouteWithLayout
            component={LoginToken}
            path={routes.login.token}
            exact
          />
          <RouteWithLayout
            component={PasswordReset}
            path={routes.password.reset}
            exact
          />
          <RouteWithLayout
            component={PasswordChange}
            path={routes.password.change}
            exact
          />
          <RouteWithLayout
            component={PasswordFirstAccess}
            path={routes.password.firstAccess}
            exact
          />
        </RouteWithLayout>

        <RouteWithLayout path={privateRoutes} layout={MainLayout} auth>
          <RouteWithLayout
            component={Contracts.Main}
            path={routes.contracts.all}
            layout={MainContentLayout}
            permission="index_contract"
            exact
            auth
          />
          <RouteWithLayout
            component={Contracts.Show}
            path={routes.contracts.show}
            layout={MainContentLayout}
            permission="show_contract"
            exact
            auth
          />
          <RouteWithLayout
            component={Contracts.Edit}
            path={routes.contracts.edit}
            layout={MainContentLayout}
            permission="update_contract"
            exact
            auth
          />
          <RouteWithLayout
            component={Contracts.New}
            path={routes.contracts.new}
            layout={MainContentLayout}
            provider={ContractProvider}
            permission="complete_contract"
            exact
            auth
          />

          <RouteWithLayout
            component={Contracts.Steps}
            path={routes.contracts.migratePlan.steps.main}
            layout={MainContentLayout}
            provider={ContractPlanMigrateProvider}
            exact
            auth
          />

          <RouteWithLayout
            component={Contracts.Logs}
            path={routes.contracts.logs}
            layout={MainContentLayout}
            admin
            exact
            auth
          />

          <RouteWithLayout
            component={ContractPlans.Logs}
            path={routes.contractPlans.logs}
            layout={MainContentLayout}
            admin
            exact
            auth
          />

          <RouteWithLayout
            component={Entries.Logs}
            path={routes.entries.logs}
            layout={MainContentLayout}
            admin
            exact
            auth
          />

          <RouteWithLayout
            component={Plans.Main}
            path={routes.plans.all}
            permission="index_plan"
            layout={MainContentLayout}
            exact
            auth
          />
          <RouteWithLayout
            component={Plans.New}
            path={routes.plans.new}
            layout={MainContentLayout}
            permission="create_plan"
            exact
            auth
          />
          <RouteWithLayout
            component={Plans.Show}
            path={routes.plans.show}
            layout={MainContentLayout}
            permission="show_plan"
            exact
            auth
          />
          <RouteWithLayout
            component={Plans.Edit}
            path={routes.plans.edit}
            layout={MainContentLayout}
            permission="update_plan"
            exact
            auth
          />
          <RouteWithLayout
            component={Plans.Logs}
            path={routes.plans.logs}
            layout={MainContentLayout}
            admin
            exact
            auth
          />

          <RouteWithLayout
            component={Clients.Main}
            path={routes.clients.all}
            layout={MainContentLayout}
            permission="index_customer"
            exact
            auth
          />
          <RouteWithLayout
            component={Clients.Show}
            path={routes.clients.show}
            layout={MainContentLayout}
            permission="show_customer"
            exact
            auth
          />
          <RouteWithLayout
            component={Clients.New}
            path={routes.clients.new}
            layout={MainContentLayout}
            provider={ClientProvider}
            permission="create_customer"
            exact
            auth
          />
          <RouteWithLayout
            component={Clients.EditData}
            path={routes.clients.edit}
            layout={MainContentLayout}
            permission="update_customer"
            exact
            auth
          />
          <RouteWithLayout
            component={Clients.EditAddress}
            path={routes.clients.editAddress}
            layout={MainContentLayout}
            permission="update_customer"
            exact
            auth
          />
          <RouteWithLayout
            component={Clients.Logs}
            path={routes.clients.logs}
            layout={MainContentLayout}
            admin
            exact
            auth
          />

          <RouteWithLayout
            component={Invoices.Main}
            path={routes.invoices.all}
            layout={MainContentLayout}
            permission="index"
            exact
            auth
          />

          <RouteWithLayout
            component={Invoices.Show}
            path={routes.invoices.show}
            layout={MainContentLayout}
            permission="show_invoice"
            exact
            auth
          />

          <RouteWithLayout
            component={Invoices.New}
            path={routes.contractInvoices.new}
            layout={MainContentLayout}
            permission="create_invoice"
            exact
            auth
          />

          <RouteWithLayout
            component={Invoices.Steps}
            path={routes.invoices.steps.main}
            layout={MainContentLayout}
            provider={DetachedInvoiceProvider}
            exact
            auth
          />

          <RouteWithLayout
            component={Invoices.Logs}
            path={routes.invoices.logs}
            layout={MainContentLayout}
            admin
            exact
            auth
          />

          <RouteWithLayout
            component={Orders.Logs}
            path={routes.orders.logs}
            layout={MainContentLayout}
            admin
            exact
            auth
          />

          <RouteWithLayout
            component={TaxInvoices.Main}
            path={routes.taxInvoices.all}
            layout={MainContentLayout}
            permission="index_service_tax_invoice"
            exact
            auth
          />
          <RouteWithLayout
            component={TaxInvoices.Show}
            path={routes.taxInvoices.show}
            layout={MainContentLayout}
            permission="show_service_tax_invoice"
            exact
            auth
          />
          <RouteWithLayout
            component={TaxInvoices.Edit}
            path={routes.taxInvoices.edit}
            layout={MainContentLayout}
            exact
            auth
          />
          <RouteWithLayout
            component={TaxInvoices.Logs}
            path={routes.taxInvoices.logs}
            layout={MainContentLayout}
            admin
            exact
            auth
          />

          <RouteWithLayout
            component={SettingsPaymentMethods.Main}
            path={routes.settings.paymentMethods.main}
            layout={MainContentLayout}
            permission="update_setting"
            exact
            auth
          />
          <RouteWithLayout
            component={SettingsPaymentMethods.Steps}
            path={routes.settings.paymentMethods.steps.main}
            layout={MainContentLayout}
            provider={PaymentMethodsProvider}
            permission="update_setting"
            auth
          />
          <RouteWithLayout
            component={SettingsPaymentMethods.Edit}
            path={routes.settings.paymentMethods.edit}
            layout={MainContentLayout}
            provider={PaymentMethodsProvider}
            permission="update_setting"
            exact
            auth
          />

          <RouteWithLayout
            component={Settings.Main}
            path={routes.settings.all}
            exact
            auth
          />
          <RouteWithLayout
            component={Settings.Logs}
            path={routes.settings.logs}
            layout={MainContentLayout}
            admin
            exact
            auth
          />

          <RouteWithLayout
            component={SettingsAccount.Logs}
            path={routes.settings.account.logs}
            layout={MainContentLayout}
            admin
            exact
            auth
          />

          <RouteWithLayout
            component={SettingsTaxes.Logs}
            path={routes.settings.taxes.logs}
            layout={MainContentLayout}
            admin
            exact
            auth
          />

          <RouteWithLayout
            component={SettingsTaxes.LogsTaxType}
            path={routes.settings.taxTypes.logs}
            layout={MainContentLayout}
            admin
            exact
            auth
          />

          <RouteWithLayout
            component={SettingsEmails.Logs}
            path={routes.settings.emails.logs}
            layout={MainContentLayout}
            admin
            exact
            auth
          />

          <RouteWithLayout
            component={SettingsUsers.New}
            path={routes.settings.users.new}
            layout={MainContentLayout}
            admin
            exact
            auth
          />
          <RouteWithLayout
            component={SettingsUsers.Edit}
            path={routes.settings.users.edit}
            layout={MainContentLayout}
            admin
            exact
            auth
          />
          <RouteWithLayout
            component={SettingsUsers.Logs}
            path={routes.settings.users.logs}
            layout={MainContentLayout}
            admin
            exact
            auth
          />

          <RouteWithLayout
            component={SettingsProfiles.New}
            path={routes.settings.profiles.new}
            layout={MainContentLayout}
            admin
            exact
            auth
          />
          <RouteWithLayout
            component={SettingsProfiles.Edit}
            path={routes.settings.profiles.edit}
            layout={MainContentLayout}
            exact
            auth
          />

          <RouteWithLayout
            component={Entries.New}
            path={routes.contracts.entries.new}
            layout={MainContentLayout}
            permission="create_entry"
            exact
            auth
          />

          <RouteWithLayout
            component={Contracts.Suspend}
            path={routes.contracts.suspend}
            layout={MainContentLayout}
            exact
            auth
          />
        </RouteWithLayout>
      </RouteWithLayout>

      <Route component={NotFound} path={routes.notFound} exact />
      <Redirect to={routes.notFound} />
    </SwitchRouterDom>
  )
}

export default Switch
